import type { Coding } from '@medplum/fhirtypes'
import {
  type DerivationRule,
  derivationInputs,
  derivedValueInUnit,
  inputDefault,
  selectedRule,
} from '#src/observation-logic/derivations/rules.ts'
import { STARTER_DERIVATIONS } from '#src/observation-logic/derivations/starter.ts'
import type { ConditionEnv } from '#src/observation-logic/evaluate.ts'
import {
  type InstrumentAnswers,
  instrumentAnswerKey,
} from '#src/observation-logic/instruments/records.ts'
import type { ObservationValue } from '#src/observation-logic/value.ts'
import {
  instrumentItem,
  instrumentItems,
} from '#src/terminology/instruments/registry.ts'
import type {
  InstrumentItem,
  InstrumentTemplate,
} from '#src/terminology/instruments/types.ts'

// A panel item a calculation fills from the panel's other answers: the
// orthostatic change from the lying and standing readings, mean arterial
// pressure from systolic and diastolic.
export interface PanelDerivedItem {
  item: InstrumentItem
  rule: DerivationRule
}

export function panelDerivedItems(
  template: InstrumentTemplate,
  rules: readonly DerivationRule[] = STARTER_DERIVATIONS
): PanelDerivedItem[] {
  return instrumentItems(template).flatMap((item) => {
    if (item.scaleType !== 'Qn' || item.scored) {
      return []
    }
    const rule = selectedRule(item.coding, rules)
    // Only a rule whose inputs are all in the panel: one reading elsewhere
    // would make the panel's value depend on the rest of the note.
    return rule && rule.inputs.every((input) => instrumentItem(template, input))
      ? [{ item, rule }]
      : []
  })
}

// The rule that fills an item, unless the clinician set it by hand.
export function panelDerivedRule(
  template: InstrumentTemplate,
  coding: Pick<Coding, 'system' | 'code'>,
  overrides: readonly string[] = [],
  rules: readonly DerivationRule[] = STARTER_DERIVATIONS
): DerivationRule | undefined {
  const key = instrumentAnswerKey(coding)
  if (overrides.includes(key)) {
    return undefined
  }
  return panelDerivedItems(template, rules).find(
    ({ item }) => instrumentAnswerKey(item.coding) === key
  )?.rule
}

interface DerivedAnswers {
  [key: string]: ObservationValue | null | undefined
}

// The answers with every calculated item brought up to date from the
// others: a value its rule computes, or none once an input is gone. An item
// the clinician set by hand (`overrides`, by answer key) keeps that value.
export function withDerivedAnswers(
  template: InstrumentTemplate,
  answers: InstrumentAnswers,
  {
    overrides = [],
    variable,
    rules = STARTER_DERIVATIONS,
  }: {
    overrides?: readonly string[]
    variable?: ConditionEnv['variable']
    rules?: readonly DerivationRule[]
  } = {}
): DerivedAnswers {
  const next: DerivedAnswers = {
    ...answers,
  }
  for (const { item, rule } of panelDerivedItems(template, rules)) {
    const key = instrumentAnswerKey(item.coding)
    if (overrides.includes(key)) {
      continue
    }
    const values = rule.inputs.map((coding) => {
      const input = instrumentItem(template, coding)
      return {
        coding,
        value:
          (input ? next[instrumentAnswerKey(input.coding)] : null) ??
          inputDefault(rule, coding)?.value ??
          null,
      }
    })
    const computed = derivedValueInUnit(
      rule,
      rule.compute(derivationInputs(values, variable ? { variable } : {})),
      item.unit
    )
    next[key] = computed?.kind === 'number' ? computed : null
  }
  return next
}

// The keys of the items a panel node's clinician set by hand.
export function panelOverrides(attrs: {
  derivedOverrides?: unknown
}): string[] {
  const raw = attrs.derivedOverrides
  return Array.isArray(raw)
    ? raw.filter((key): key is string => typeof key === 'string')
    : []
}
